import { useEffect, useState } from 'react';
import { Project } from 'core/models/project';
import { Vehicle } from 'core/models/vehicle';
import { MessageSource as MQTTMessageSource } from '@tier4/autowareviz-extension-ct-message-source';
import { useVehicleRepository } from 'services/useVehicleRepository';
import { useVisualizeEngine } from './useVisualizeEngine';
import { cancellable } from 'services/utility/cancellable';
import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';

function useRawWebSocket(stableWebSocket: StableWebSocket | undefined) {
    const [disconnectedAt, setDisconnectAt] = useState<number>(0);
    useEffect(() => {
        if (!stableWebSocket) return;

        const listener = () => {
            setDisconnectAt(Date.now());
        };
        stableWebSocket.addEventListener('close', listener);
        return () => stableWebSocket.removeEventListener('close', listener);
    }, [stableWebSocket]);

    const [ws, setWS] = useState<WebSocket>();
    useEffect(() => {
        if (!stableWebSocket) return;

        return cancellable(async isCancel => {
            const ws = await stableWebSocket.get();
            if (isCancel()) return;
            setWS(ws);
        });
    }, [stableWebSocket, disconnectedAt]);

    return ws;
}

export function useAutowareVizOverMQTT(vehicleId: Vehicle.Id, projectId: Project.Id) {
    const engine = useVisualizeEngine();

    const repos = useVehicleRepository();
    const ws = useRawWebSocket(repos?.getWebSocket?.());

    const [error, setError] = useState<unknown>();
    useEffect(() => {
        if (!error) return;
        console.error(error);
    }, [error]);

    useEffect(() => {
        if (!engine) return;
        if (!ws) return;

        engine.connect(new MQTTMessageSource(ws, vehicleId, projectId, setError));
        return () => {
            engine.disconnect();
        };
    }, [engine, projectId, vehicleId, ws]);

    return engine;
}
